import React, { useMemo, useState } from "react";
import moment from "moment";

import Icon from "../../../components/Icon";
import { convertIntToMoney } from "../../../helpers/conversions";

const formatMonth = month => moment(month, 'YYYY-MM').locale('pt-br').format('MMM/YYYY');

const collectGroupIds = nodes => nodes.reduce((ids, node) => (
    node.children?.length ? [...ids, node.id, ...collectGroupIds(node.children)] : ids
), []);

/**
 * Fluxo de caixa mensal: plano de contas nas linhas, meses nas colunas, cada
 * mês aberto em previsto × realizado. Os valores chegam do backend já com
 * sinal (pagamento negativo), então a leitura vertical fecha: saldo anterior +
 * recebimentos + pagamentos = saldo final.
 */
export const CashFlowMatrix = ({ data, loading, showTransfers }) => {
    const months = data?.months ?? [];

    const [expanded, setExpanded] = useState([]);

    const groupIds = useMemo(() => collectGroupIds([
        ...(data?.income?.categories ?? []),
        ...(data?.expense?.categories ?? []),
    ]), [data]);

    const allExpanded = groupIds.length > 0 && groupIds.every(id => expanded.includes(id));

    const toggle = id => setExpanded(current => (
        current.includes(id) ? current.filter(item => item !== id) : [...current, id]
    ));

    const columnCount = 1 + (months.length * 2);

    const renderValues = (values, key) => months.map((month, index) => (
        <React.Fragment key={`${key}-${month}`}>
            <td className="cell-expected">{convertIntToMoney(values?.[index]?.expected ?? 0)}</td>
            <td>{convertIntToMoney(values?.[index]?.realized ?? 0)}</td>
        </React.Fragment>
    ));

    const renderNodes = (nodes, side, depth = 0) => nodes.flatMap(node => {
        const hasChildren = node.children?.length > 0;
        const isOpen = expanded.includes(node.id);
        const key = node.id ?? `${side}-uncategorized`;

        const row = (
            <tr key={key} className={`matrix-row matrix-row--${side}${hasChildren ? ' is-group' : ''}`}>
                <th scope="row" className="matrix-label" style={{ paddingLeft: 20 + (depth * 22) }}>
                    {hasChildren
                        ? <button type="button" className="matrix-toggle" onClick={() => toggle(node.id)}>
                            <Icon name={isOpen ? 'arrow-dropdown' : 'arrow'} size={18} />
                        </button>
                        : <span className="matrix-toggle-placeholder" />
                    }
                    {node.code ? <span className="matrix-code">{node.code}</span> : null}
                    {node.name}
                </th>
                {renderValues(node.values, key)}
            </tr>
        );

        return hasChildren && isOpen ? [row, ...renderNodes(node.children, side, depth + 1)] : [row];
    });

    if (!loading && months.length === 0) {
        return <div className="cash-flow-matrix-empty">Nenhum resultado encontrado</div>;
    }

    return (
        <div className="cash-flow-matrix">
            <div className="matrix-toolbar">
                <button type="button" className="matrix-expand-all" onClick={() => setExpanded(allExpanded ? [] : groupIds)}>
                    {allExpanded ? 'Recolher tudo' : 'Expandir tudo'}
                </button>
            </div>

            <div className="matrix-scroll">
                <table>
                    <thead>
                        <tr className="matrix-months">
                            <th className="matrix-label matrix-corner" rowSpan={2}>Categoria</th>
                            {months.map(month => (
                                <th key={month} colSpan={2} className="matrix-month">{formatMonth(month)}</th>
                            ))}
                        </tr>
                        <tr className="matrix-columns">
                            {months.map(month => (
                                <React.Fragment key={month}>
                                    <th className="cell-expected">Previsto</th>
                                    <th>Realizado</th>
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading &&
                            <tr>
                                <td className="matrix-loading" colSpan={columnCount}>Carregando...</td>
                            </tr>
                        }

                        {!loading && <>
                            <tr className="matrix-row matrix-row--balance">
                                <th scope="row" className="matrix-label">Saldo do mês anterior</th>
                                {renderValues(data.opening_balance, 'opening')}
                            </tr>

                            <tr className="matrix-row matrix-row--total matrix-row--income">
                                <th scope="row" className="matrix-label">Total de recebimentos</th>
                                {renderValues(data.income.total, 'income-total')}
                            </tr>
                            {renderNodes(data.income.categories, 'income')}

                            <tr className="matrix-row matrix-row--total matrix-row--expense">
                                <th scope="row" className="matrix-label">Total de pagamentos</th>
                                {renderValues(data.expense.total, 'expense-total')}
                            </tr>
                            {renderNodes(data.expense.categories, 'expense')}

                            {showTransfers &&
                                <tr className="matrix-row matrix-row--transfers">
                                    <th scope="row" className="matrix-label">Transferências entre contas</th>
                                    {months.map((month, index) => (
                                        <React.Fragment key={`transfers-${month}`}>
                                            <td className="cell-expected">—</td>
                                            <td>{convertIntToMoney(data.transfers?.[index] ?? 0)}</td>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            }

                            <tr className="matrix-row matrix-row--balance matrix-row--closing">
                                <th scope="row" className="matrix-label">Saldo final</th>
                                {renderValues(data.closing_balance, 'closing')}
                            </tr>
                        </>}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CashFlowMatrix;
